import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Zap,
  Users,
  Wallet,
  Boxes,
  FileText,
} from "lucide-react";
import { KineticHeadline } from "./motion";

const AUTH_URL = "https://app.muft.space/auth/login";
const WEB_APP_URL = "https://app.muft.space";

/**
 * Micro-interação do Talos: exemplo prático de prompt + resposta gerada pela IA,
 * com indicador de digitação, chips clicáveis e rotação automática de cenários.
 */
const TALOS_EXAMPLES = [
  {
    key: "caixa",
    chip: "Saldo do caixa",
    icon: Wallet,
    prompt: "Talos, qual o saldo do caixa hoje?",
    reply: (
      <>
        Saldo consolidado hoje: <strong>R$ 4.820,00</strong> em 3 filiais. Entraram R$ 5.120,00 e
        saíram R$ 1.835,50. Deseja que eu gere o relatório do dia?
      </>
    ),
  },
  {
    key: "estoque",
    chip: "Estoque da semana",
    icon: Boxes,
    prompt: "Talos, o que vai faltar essa semana?",
    reply: (
      <>
        Pelo ritmo dos últimos 14 dias, você vai precisar repor <strong>Coca-Cola 2L</strong> (3
        dias) e <strong>Café 500g</strong> (5 dias). Posso emitir o pedido de compra?
      </>
    ),
  },
  {
    key: "fiscal",
    chip: "Alertas fiscais",
    icon: FileText,
    prompt: "Talos, tem nota rejeitada na SEFAZ?",
    reply: (
      <>
        Encontrei <strong>1 rejeição</strong>: nota 1287 (Rejeição 539 · CFOP incorreto). Quer que
        eu ajuste o CFOP e reenvie agora?
      </>
    ),
  },
];

const LiveChatMock = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [isTyping, setIsTyping] = useState(false);

  const active = TALOS_EXAMPLES[activeIdx];

  const showExample = (idx) => {
    setIsTyping(true);
    window.setTimeout(() => {
      setActiveIdx(idx);
      setIsTyping(false);
    }, 550);
  };

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIsTyping(true);
      window.setTimeout(() => {
        setActiveIdx((prev) => (prev + 1) % TALOS_EXAMPLES.length);
        setIsTyping(false);
      }, 550);
    }, 7000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="w-full rounded-2xl bg-white p-5 shadow-2xl border border-navy-100 dark:border-white/10 dark:bg-navy-900">
      <div className="mb-4 flex items-center justify-between border-b border-navy-50 dark:border-white/10 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-navy-800 text-lavender shadow-sm">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <p className="text-[11px] font-extrabold text-navy-800 dark:text-white flex items-center gap-1.5">
              Talos · IA MUFT
            </p>
            <p className="text-[9px] font-semibold text-emerald-500 flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> online
            </p>
          </div>
        </div>
        <span className="rounded-full bg-lavender/40 dark:bg-white/10 px-2.5 py-1 text-[9px] font-bold uppercase tracking-widest text-navy-800 dark:text-lavender">
          IA Operacional
        </span>
      </div>

      {/* Chips: troque de exemplo — micro-interação */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {TALOS_EXAMPLES.map((ex, idx) => {
          const Icon = ex.icon;
          return (
            <button
              key={ex.key}
              type="button"
              onClick={() => showExample(idx)}
              data-testid={"hero-talos-chip-" + ex.key}
              className={
                "flex items-center gap-1 rounded-full px-2.5 py-1 text-[9px] font-bold transition-all duration-200 " +
                (idx === activeIdx
                  ? "bg-navy-800 text-white shadow-sm dark:bg-white dark:text-navy-900"
                  : "border border-navy-100 bg-ice text-navy-500 hover:border-navy-200 dark:border-white/10 dark:bg-navy-800/60 dark:text-navy-100/70")
              }
            >
              <Icon className="h-2.5 w-2.5" />
              {ex.chip}
            </button>
          );
        })}
      </div>

      <div className="space-y-2.5 text-left min-h-[132px]">
        {/* Prompt do usuário */}
        <div className="flex justify-end">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.key}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="max-w-[85%] rounded-2xl rounded-br-sm bg-navy-800 px-3.5 py-2 text-[11px] font-medium text-white shadow-sm"
              data-testid="hero-talos-prompt"
            >
              {active.prompt}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Resposta da IA — com micro-interação de digitação */}
        <div className="flex items-start gap-2">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-navy-800 text-lavender mt-0.5">
            <Sparkles className="h-3 w-3" />
          </div>
          <div className="max-w-[85%] rounded-2xl rounded-bl-sm border border-navy-100 bg-ice dark:border-white/10 dark:bg-navy-800 px-3.5 py-2 text-[11px] font-medium leading-relaxed text-navy-800 dark:text-white/90">
            {isTyping ? (
              <span className="flex items-center gap-1 py-1" data-testid="hero-talos-typing">
                {[0, 1, 2].map((d) => (
                  <span
                    key={d}
                    className="h-1.5 w-1.5 rounded-full bg-lavender animate-bounce"
                    style={{ animationDelay: `${d * 0.12}s` }}
                  />
                ))}
              </span>
            ) : (
              <AnimatePresence mode="wait">
                <motion.span
                  key={active.key}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  data-testid="hero-talos-reply"
                >
                  {active.reply}
                </motion.span>
              </AnimatePresence>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        {[
          { l: "Vendas hoje", v: "R$ 4,8k" },
          { l: "Filiais ativas", v: "1 a 30" },
          { l: "Equipe", v: "Ilimitada" },
        ].map((s) => (
          <div key={s.l} className="rounded-xl bg-ice dark:bg-navy-800/80 p-2.5 text-center border border-navy-50 dark:border-white/5">
            <p className="text-[8px] font-bold uppercase tracking-wider text-navy-400 dark:text-navy-200/60">
              {s.l}
            </p>
            <p className="text-[13px] font-black text-navy-800 dark:text-white mt-0.5">{s.v}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Hero = ({ onStartTrial }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacityHero = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[100svh] overflow-hidden bg-[radial-gradient(ellipse_120%_90%_at_50%_-10%,#1a0573_0%,#120052_45%,#0a0033_100%)] text-white"
      data-testid="hero-section"
    >
      {/* Background Ambience */}
      <motion.div
        style={{ y: yBg }}
        className="pointer-events-none absolute inset-0 -z-10"
      >
        <div className="absolute -top-32 left-1/2 h-[520px] w-[880px] -translate-x-1/2 rounded-full bg-navy-400/20 blur-[130px]" />
        <div className="absolute top-1/3 left-10 h-72 w-72 rounded-full bg-sky/15 blur-[120px]" />
        <div className="absolute top-1/2 right-10 h-96 w-96 rounded-full bg-lavender/10 blur-[140px]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:48px_48px]" />
      </motion.div>

      <motion.div
        style={{ opacity: opacityHero }}
        className="relative mx-auto flex max-w-7xl flex-col items-center px-6 pt-32 pb-20 text-center md:px-12 md:pt-40 md:pb-28"
      >
        {/* Top Announcement Badge */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider backdrop-blur-md"
          data-testid="hero-eyebrow-badge"
        >
          <span className="flex h-2 w-2 rounded-full bg-emerald-400 animate-ping" />
          <span className="text-white/90">
            PLATAFORMA ALL-IN-ONE - ERP + PDV + CRM + PONTO DIGITAL + CONTABILIDADE EXPERT COM TALOS IA
          </span>
        </motion.div>

        {/* Dynamic Kinetic Headline */}
        <KineticHeadline
          className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.08] max-w-5xl lg:max-w-6xl"
          lineClassName="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.08]"
          lines={[
            {
              words: [
                { text: "O", emphasis: false },
                { text: "aplicativo", emphasis: true },
                { text: "que", emphasis: false },
              ],
            },
            {
              words: [
                { text: "unifica", emphasis: false },
                { text: "os", emphasis: false },
                { text: "dados", emphasis: true },
              ],
            },
            {
              words: [
                { text: "da", emphasis: false },
                { text: "sua", emphasis: false },
                { text: "operação", emphasis: false },
              ],
            },
          ]}
        />

        {/* Primary CTA — único botão de ação abaixo do título */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="mt-9 flex flex-col items-center gap-4 sm:flex-row"
        >
          <a
            href={AUTH_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-primary-cta"
            className="group flex items-center gap-3 rounded-full bg-white px-10 py-5 text-lg font-black text-navy-800 shadow-[0_25px_60px_-15px_rgba(255,255,255,0.5)] transition-all duration-300 hover:bg-lavender hover:scale-105"
          >
            Ir para o app
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </motion.div>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mt-8 max-w-2xl text-base font-medium leading-relaxed text-white/70 sm:text-lg md:text-xl"
        >
          Chega de pagar por dezenas de ferramentas separadas. O MUFT reúne{" "}
          <strong className="text-white font-bold">ERP, PDV, CRM, Ponto Digital e Contabilidade Expert</strong> em um aplicativo só, com{" "}
          <span className="text-lavender font-bold">usuários ilimitados</span> e a inteligência do{" "}
          <strong className="text-white font-bold">Talos</strong> operando o sistema por você.
        </motion.p>

        {/* Reassurance Micro-Copy */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.65 }}
          className="mt-6 flex flex-wrap items-center justify-center gap-6 text-xs font-semibold text-white/55"
        >
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4 text-emerald-400" /> Acesso imediato
          </span>
          <span className="flex items-center gap-1.5">
            <Users className="h-4 w-4 text-lavender" /> Usuários e equipe ilimitados
          </span>
          <span className="flex items-center gap-1.5">
            <Zap className="h-4 w-4 text-sky" /> Provedor de Tecnologia Meta
          </span>
          <span className="flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-emerald-400" /> WhatsApp em 1 clique
          </span>
        </motion.div>

        {/* Mockup notebook + smartphone — tela inicial do app */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 w-full max-w-4xl"
          data-testid="hero-device-mockup"
        >
          <div className="relative mx-auto flex items-end justify-center">
            {/* Notebook */}
            <div className="w-full max-w-2xl rounded-2xl border border-white/15 bg-white/10 p-2.5 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] backdrop-blur-xl">
              <div className="overflow-hidden rounded-xl bg-white">
                {/* App topbar */}
                <div className="flex items-center gap-2.5 bg-navy-800 px-3.5 py-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-white">MUFT</span>
                  <span className="flex-1 rounded-full bg-white/10 px-3 py-1 text-[8px] font-medium text-white/60">
                    Buscar colaborador, produto ou cliente…
                  </span>
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-lavender text-[8px] font-bold text-navy-900">
                    MV
                  </span>
                </div>
                {/* App body */}
                <div className="flex min-h-[168px]">
                  <div className="flex w-11 flex-col gap-2 border-r border-navy-50 bg-ice p-2.5">
                    <span className="h-1.5 rounded-full bg-navy-800" />
                    <span className="h-1.5 rounded-full bg-navy-100" />
                    <span className="h-1.5 rounded-full bg-navy-100" />
                    <span className="h-1.5 rounded-full bg-navy-100" />
                  </div>
                  <div className="flex-1 bg-[#FBFBFE] p-3">
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { l: "Folha do mês", v: "R$ 182k", t: "+4,2%" },
                        { l: "Colaboradores", v: "142", t: "+6" },
                        { l: "eSocial", v: "100%", t: "OK" },
                      ].map((k) => (
                        <div key={k.l} className="rounded-lg border border-navy-50 bg-white p-2">
                          <p className="text-[7px] font-bold uppercase tracking-wide text-navy-400">{k.l}</p>
                          <p className="text-[11px] font-black text-navy-800">{k.v}</p>
                          <p className="text-[7px] font-bold text-emerald-500">{k.t}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2.5 rounded-lg border border-navy-50 bg-white p-2.5">
                      <div className="flex items-center justify-between">
                        <p className="text-[8px] font-bold text-navy-700">Custos da operação</p>
                        <span className="rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[6px] font-bold text-emerald-600">
                          Tempo real
                        </span>
                      </div>
                      <div className="mt-2 flex h-12 items-end gap-1.5">
                        {[38, 56, 44, 72, 60, 88, 76].map((h, idx) => (
                          <span
                            key={idx}
                            className="flex-1 rounded-t bg-gradient-to-t from-navy-800 to-sky"
                            style={{ height: `${h}%` }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mx-auto mt-1.5 h-1.5 w-4/5 rounded-full bg-white/20" />
            </div>

            {/* Smartphone — tela inicial do app */}
            <div className="absolute -bottom-5 right-0 w-[132px] rounded-[22px] border-[6px] border-navy-900 bg-navy-900 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.8)]">
              <div className="rounded-[16px] bg-gradient-to-b from-[#F4F6FF] to-white px-2.5 pb-2.5 pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[8px] font-black text-navy-800">Olá, Marina 👋</p>
                    <p className="text-[6px] font-semibold text-navy-400">Tela inicial · MUFT</p>
                  </div>
                  <span className="rounded-full bg-navy-800 px-1.5 py-0.5 text-[6px] font-bold text-lavender">
                    Talos
                  </span>
                </div>
                <div className="mt-2 rounded-xl bg-navy-800 px-2.5 py-2">
                  <p className="text-[6px] font-semibold text-white/60">Saldo do caixa</p>
                  <p className="text-[11px] font-black text-white">R$ 4.820,00</p>
                  <p className="text-[6px] font-bold text-emerald-400">▲ hoje em 3 filiais</p>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-1">
                  {["Holerite", "Folha", "eSocial"].map((a) => (
                    <span
                      key={a}
                      className="rounded-lg border border-navy-50 bg-white py-1 text-center text-[6px] font-bold text-navy-700"
                    >
                      {a}
                    </span>
                  ))}
                </div>
                <div className="mt-2 space-y-1">
                  {[
                    ["Fechamento da folha", "Pronto"],
                    ["13º Salário", "Calculado"],
                  ].map(([l, v]) => (
                    <div key={l} className="flex items-center justify-between rounded-lg border border-navy-50 bg-white px-2 py-1">
                      <span className="text-[6px] font-medium text-navy-400">{l}</span>
                      <strong className="text-[6px] font-black text-navy-800">{v}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Visual Live Interactive Showcase */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 w-full max-w-4xl"
        >
          <div className="relative mx-auto rounded-3xl border border-white/15 bg-gradient-to-b from-white/10 to-white/[0.02] p-3 shadow-[0_30px_100px_-20px_rgba(0,0,0,0.8)] backdrop-blur-2xl md:p-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 items-center">
              <div className="text-left p-4 space-y-4">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-lavender">
                  <Sparkles className="h-3 w-3" /> Assistente Nativo
                </div>
                <h3 className="text-2xl md:text-3xl font-black text-white leading-tight">
                  Converse com seu ERP em linguagem natural.
                </h3>
                <p className="text-xs md:text-sm text-white/70 leading-relaxed font-medium">
                  Peça relatórios, controle fluxo de caixa, verifique alertas fiscais e monitore a jornada dos colaboradores através do Talos.
                </p>
                <div className="flex items-center gap-3 pt-2">
                  <a
                    href="#talos"
                    className="inline-flex items-center gap-1 text-xs font-bold text-lavender hover:underline"
                  >
                    Ver todas as 9 capacidades do Talos →
                  </a>
                </div>
              </div>

              <div>
                <LiveChatMock />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
